import type { ObjectiveMappingManager } from '@/state/experiments/useObjectiveMapping';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

type MappingPromptProps = { manager: ObjectiveMappingManager; role: 'primary' | 'secondary' };

/**
 * Asks, under the picker it belongs to, whether an event just picked should also be mapped on the
 * website. Inline rather than a toast so it waits for an answer.
 */
export const MappingPrompt = ({ manager, role }: MappingPromptProps) => {
  const { prompt, isSaving } = manager;
  if (!prompt || prompt.role !== role) return null;

  return (
    <div
      role="status"
      className="border-line bg-panel2 flex w-full max-w-[560px] flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2"
    >
      <Info className="text-dim size-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
      <p className="supporting min-w-0 flex-1">
        <span className="text-foreground font-mono">{prompt.name}</span> isn&apos;t mapped on this website. Map it as{' '}
        {role} so it is offered first next time
        {role === 'secondary' ? ' and added to new experiments' : ''}?
      </p>
      <div className="flex shrink-0 gap-2">
        <Button variant="ghost" size="sm" className="rounded-lg" onClick={manager.dismiss} disabled={isSaving}>
          Not now
        </Button>
        <Button size="sm" className="rounded-lg" onClick={() => void manager.accept()} disabled={isSaving}>
          Map as {role}
        </Button>
      </div>
    </div>
  );
};
